import React, { useState, useEffect } from 'react';
import styles from './styles.module.css';
import { axiosAuthFetch } from '../../../../helpers/axios';
import { API_BASE_URL } from '../../../../constants';
import { GetAllIngredients, notifyIngredientsChanged } from '../../../../hooks/ingredients';
import { NUTRITION_BASIS, parseDecimal, toNutritionBasis } from '../../../../helpers/nutrition';
import Loader from '../../loader';
import Modal from 'react-bootstrap/Modal';
//import { getLocalStorageItem } from '../../storage';

export default function CreateIngredient() {
    const [ingredientName, setIngredientName] = useState('');
    // const [description, setDescription] = useState('');
    const [protein, setProtein] = useState(0);
    const [carbs, setCarbs] = useState(0);
    const [fat, setFat] = useState(0);
    const [kcals, setKcals] = useState(0);
    const [amount, setAmount] = useState(NUTRITION_BASIS.g); // Mengden næringsverdiene gjelder for
    const [amountEdited, setAmountEdited] = useState(false);
    const [unit, setUnit] = useState('g'); // Standard enhet
    const [ingredientArray, setIngredientArray] = useState([]);
    const [searchText, setSearchText] = useState('');
    //eslint-disable-next-line
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    //const userInfo = getLocalStorageItem('limitless-user');
    //const isAdmin = userInfo.user.isAdmin;

    async function getIngredients() {
        try {
            setIsLoading(true);
            const data = await GetAllIngredients();
            setIngredientArray(data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getIngredients();
        //eslint-disable-next-line
    }, []);

    const resetForm = () => {
        setIngredientName('');
        setProtein(0);
        setCarbs(0);
        setFat(0);
        setKcals(0);
        setAmount(NUTRITION_BASIS.g);
        setAmountEdited(false);
        setUnit('g');
    };

    const handleAmountChange = (value) => {
        setAmount(value);
        setAmountEdited(true);
    };

    // Standardmengden følger enheten (100 g / 1 dl / 1 stk) så lenge coachen ikke har skrevet inn en egen
    const handleUnitChange = (newUnit) => {
        setUnit(newUnit);
        if (!amountEdited) setAmount(NUTRITION_BASIS[newUnit]);
    };

    const parsedAmount = parseDecimal(String(amount));
    const isAmountValid = parsedAmount > 0;

    const handleSubmit = async () => {
        if (!isAmountValid) {
            alert('Mengde må være et tall større enn 0.');
            return;
        }

        const entered = {
            protein: parseDecimal(String(protein)),
            fat: parseDecimal(String(fat)),
            carbs: parseDecimal(String(carbs)),
            kcals: parseDecimal(String(kcals)),
        };
        if (Object.values(entered).some((value) => Number.isNaN(value))) {
            alert('Protein, karbohydrater, fett og kalorier må være tall (0 eller høyere).');
            return;
        }

        try {
            // Næringsverdiene er oppgitt for `parsedAmount` `unit`. De lagres omregnet til
            // 100 g / 1 dl / 1 stk, som er det alle beregninger i appen bruker.
            const stored = toNutritionBasis({ macros: entered, amount: parsedAmount, unit });
            const data = {
                name: ingredientName,
                macros: [stored.macros],
                amount: stored.amount, // Mengde
                unit,   // Enhet
            };

            await axiosAuthFetch(`${API_BASE_URL}/ingredients`, { data }, 'POST');
            alert(
                parsedAmount === stored.amount
                    ? `Ingrediens lagt til! ${entered.kcals} kcal per ${stored.amount} ${unit}`
                    : `Ingrediens lagt til! ${entered.kcals} kcal per ${parsedAmount} ${unit} (lagret som ${stored.macros.kcals} kcal per ${stored.amount} ${unit})`
            );
            // Oppdater alle ingrediens-velgere som allerede er åpne, og vår egen liste
            notifyIngredientsChanged();
            resetForm();
            await getIngredients();
        } catch (error) {
            console.error('Error adding ingredient:', error);
            alert('Kunne ikke lagre ingrediensen. Prøv igjen.');
        }
    };

    // const handleDeleteIngredient = async () => {
    //     if (window.confirm("Er du sikker på at du vil slette denne ingrediensen?")) {
    //         try {
    //             await axiosAuthFetch(`${API_BASE_URL}/ingredients/${selectedIngredient.id}`, {}, 'DELETE');
    //             alert('Ingrediens slettet!');
    //             setIngredientArray(prev => prev.filter(ing => ing.id !== selectedIngredient.id));
    //             handleCloseModal();
    //         } catch (error) {
    //             console.error("Error deleting ingredient:", error);
    //             alert('Kunne ikke slette ingrediens. Vennligst prøv igjen.');
    //         }
    //     }
    // };

    const handleIngredientClick = (ingredient) => {
        setSelectedIngredient(ingredient);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedIngredient(null);
    };

    const filteredIngredients = searchText
    ? ingredientArray.filter((ingredient) =>
        ingredient.attributes?.name?.toLowerCase().includes(searchText.toLowerCase())
      )
    : [];

    if(isLoading) {
        return(<Loader />)
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className="text-center">Ingredienser</h2>
                <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Søk etter ingrediens"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                {searchText && ingredientArray.length > 0 && (
                    <div className={styles.ingredientList}>
                        {filteredIngredients.map((ingredient) => (
                            <div 
                                key={ingredient.id} 
                                onClick={() => handleIngredientClick(ingredient)}
                                className={styles.ingredientItem}
                            >
                                {ingredient.attributes.name}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                <label className={styles.label}>
                    Navn:
                    <input
                        className={styles.input}
                        value={ingredientName}
                        placeholder="Navn"
                        onChange={(e) => setIngredientName(e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    Næringsinnholdet gjelder for mengde:
                    <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        value={amount}
                        placeholder="Mengde"
                        onChange={(e) => handleAmountChange(e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    Enhet:
                    <select
                        className={styles.select}
                        value={unit}
                        onChange={(e) => handleUnitChange(e.target.value)}
                    >
                        <option value="g">gram</option>
                        <option value="dl">dl</option>
                        <option value="stk">stk</option>
                    </select>
                </label>
                <p>
                    {isAmountValid
                        ? `Verdiene under gjelder for ${String(amount).trim()} ${unit}. I et måltid oppgis mengden i ${unit}.`
                        : 'Mengde må være et tall større enn 0.'}
                </p>
                <label className={styles.label}>
                    Protein (g):
                    <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        value={protein}
                        placeholder="Protein"
                        onChange={(e) => setProtein(e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    Karbohydrater (g):
                    <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        value={carbs}
                        placeholder="Karbohydrater"
                        onChange={(e) => setCarbs(e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    Fett (g):
                    <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        value={fat}
                        placeholder="Fett"
                        onChange={(e) => setFat(e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    Kalorier (kcal):
                    <input
                        className={styles.input}
                        type="text"
                        inputMode="decimal"
                        value={kcals}
                        placeholder="Kalorier"
                        onChange={(e) => setKcals(e.target.value)}
                    />
                </label>
                <div className={styles.button} onClick={handleSubmit}>Send</div>
            </form>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedIngredient?.attributes?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><strong>Protein:</strong> {selectedIngredient?.attributes?.macros[0]?.protein} g</p>
                    <p><strong>Fett:</strong> {selectedIngredient?.attributes?.macros[0]?.fat} g</p>
                    <p><strong>Karbohydrater:</strong> {selectedIngredient?.attributes?.macros[0]?.carbs} g</p>
                    <p><strong>Kalorier:</strong> {selectedIngredient?.attributes?.macros[0]?.kcals} kcals</p>
                    {/* Beregningene bruker alltid verdiene per 100 g / 1 dl / 1 stk */}
                    <p><strong>Gjelder for:</strong> {NUTRITION_BASIS[selectedIngredient?.attributes?.unit]} {selectedIngredient?.attributes?.unit}</p>
                    {selectedIngredient?.attributes?.amount !== NUTRITION_BASIS[selectedIngredient?.attributes?.unit] && (
                        <p><strong>Registrert mengde:</strong> {selectedIngredient?.attributes?.amount} {selectedIngredient?.attributes?.unit} (brukes ikke i beregninger)</p>
                    )}
                    {/* {isAdmin && (
                        <>
                            <button className={styles.deleteButton} onClick={handleDeleteIngredient}>Slett</button>
                        </>
                    )} */}
                </Modal.Body>
                <Modal.Footer>
                    <button className={styles.closeButton} onClick={handleCloseModal}>Lukk</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}